import {kebabCase} from 'lodash';
import {fromJS, List} from 'immutable';
import notifier from '@albert-io/notifier';
import constants from 'client/constants';

import type {SubjectViewQuery} from './SubjectView.queries.generated';

type GqlSubject = NonNullable<NonNullable<SubjectViewQuery['subjects']>['results']>[number];

/**
 * Wraps a GraphQL subject response to satisfy the SubjectModelV2 method interface
 * that SubjectView's children expect. This lets us migrate SubjectView's data fetching
 * to GQL without changing any child components.
 */
export function createSubjectViewAdapter(subject: GqlSubject) {
  return {
    getId: () => subject.id,
    getName: () => subject.name,
    getDescription: () => subject.description ?? '',
    get: (key: string) => {
      if (key === 'name') return subject.name;
      return undefined;
    },
    isHidden: () => subject.hidden,
    getUrlSlug: () => subject.urlSlug,
    getBanner: () => subject.banner ?? null,
    getLogoLocation: () => ({
      path: subject.logoImageUrl ?? '',
      host: constants.SUPPLEMENTS_ORIGIN,
      href: `${constants.SUPPLEMENTS_ORIGIN}/${subject.logoImageUrl ?? ''}`
    }),
    getCurriculumAreaSlug: () =>
      subject.curriculumArea ? kebabCase(subject.curriculumArea.name) : 'default',
    getGuides: () =>
      List(
        subject.guides.map((guide) => ({
          getId: () => guide.id,
          getGuideType: () => guide.type
        }))
      ),
    getSubjectFaqs: () => List(subject.subjectFaqs),
    doesUserHaveAccess: () => subject.userHasAccess ?? false,
    getSubscriptionsPrices: () => {
      const raw = subject.subscriptionsPrices ?? [];
      const parsed = raw
        .map((entry) => {
          if (entry == null) return null;
          if (typeof entry !== 'string') return entry;
          try {
            return JSON.parse(entry);
          } catch (error) {
            notifier.notify(error as Error, {
              message: 'Failed to parse subscriptionsPrices JsonString',
              tags: 'SubjectView',
              severity: 'warning',
              context: {subjectId: subject.id, entry}
            });
            return null;
          }
        })
        .filter((entry): entry is Record<string, unknown> => entry != null);
      return fromJS(parsed);
    },
    getMeta: () => ({
      isUserHasAccess: () => subject.userHasAccess ?? false,
      hasAccessViaSubscription: () => subject.hasAccessViaSubscription ?? false,
      getCountOfQuestions: () => subject.countOfQuestions ?? 0,
      getCountOfFreeResponseQuestions: () => subject.countOfFreeResponseQuestions ?? 0
    })
  };
}

export type SubjectAdapter = ReturnType<typeof createSubjectViewAdapter>;
